/**
 * 開発者セッションの状態（ログイン中か・誰か）。トークン本体は api.ts が localStorage に持つ。
 *
 * 1 つの KUMODeck アカウント（2026-10-07 console/one-account・旗 oneAccount = platform/info）:
 *   旗が on なら、作り手の鍵（developer）に加えてアカウントの鍵（account = kas_・accountApi.ts）も持つ。
 *   作り手でない人は account だけ（Player の画面と「招待コードを入れる」画面だけが開く = App.tsx）。
 *   旗が off なら account は常に null（残っていた kas_ も捨てる）= 今までと同じ動き。
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, getToken, onUnauthorized, setToken, type Developer } from './api';
import { getAccountMe, getAccountToken, logoutAccount, setAccountToken } from './accountApi';
import { loadOneAccountInfo } from './devOAuthClient';
import { ONE_ACCOUNT_OFF, type ConsoleSession, type OneAccountInfo } from './oneAccount';

/** アカウント（人）の側。token = kas_（Player の口だけに効く） */
export interface AccountSession {
  token: string;
  email: string | null;
  displayName: string | null;
}

interface SessionValue {
  developer: Developer | null;
  account: AccountSession | null;
  /** 旗（platform/info）。読み込み前・失敗は off */
  oneAccount: OneAccountInfo;
  ready: boolean;
  signIn: (token: string, developer: Developer) => void;
  /** account からの戻り（POST /v1/account/console/session の応答）で入る。作り手の鍵があれば作り手の情報も読む */
  signInConsole: (s: ConsoleSession) => Promise<void>;
  /** kas_ が切れた（401）とき。作り手の鍵は残す（作り手の画面は続けて使える） */
  dropAccount: () => void;
  signOut: () => Promise<void>;
  /** 表示中の開発者情報の一部を差し替える（メール確認の完了を帯に反映する等） */
  updateDeveloper: (patch: Partial<Developer>) => void;
  updateAccount: (patch: Partial<Omit<AccountSession, 'token'>>) => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [developer, setDeveloper] = useState<Developer | null>(null);
  const [account, setAccount] = useState<AccountSession | null>(null);
  const [oneAccount, setOneAccount] = useState<OneAccountInfo>(ONE_ACCOUNT_OFF);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const devLoad = getToken()
      ? api<{ developer: Developer }>('/v1/developers/me')
          .then((r) => setDeveloper(r.developer))
          .catch(() => setToken(null))
      : Promise.resolve();
    // 旗はいつも先に読む（ログインの画面を今の形と account へ行く形のどちらで出すかが要る）。WHY 待つか: 待たないと今のログインの画面が
    // 一瞬出てから切り替わる。platform/info はログインの画面も読む 1 本（devOAuthClient.ts で使い回す）= 起動に足すのはこの 1 本だけ。
    // アカウントの鍵は旗が on のときだけ確かめる: off に戻した後に残った kas_ で Player の画面を出さない（off = 今と同じ）
    const kas = getAccountToken();
    const accountLoad = loadOneAccountInfo().then(async (info) => {
      setOneAccount(info);
      if (!kas) return;
      if (!info.enabled) return setAccountToken(null);
      try {
        const me = await getAccountMe(kas);
        setAccount({ token: kas, email: me.account.email, displayName: me.account.displayName });
      } catch (e) {
        // 401（切れた・止められた）・404（旗 off）→ 捨てる（もう一度 account で入れば戻る）。つながらないだけなら端末には残す（次の起動で読める）
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) setAccountToken(null);
      }
    });
    void Promise.all([devLoad, accountLoad]).finally(() => setReady(true));
  }, []);

  // どの画面でも 401 が返ったらセッション切れとしてログインへ戻す（作り手の鍵の 401 = api.ts。kas_ の 401 は呼んだ画面が dropAccount）
  useEffect(
    () =>
      onUnauthorized(() => {
        setToken(null);
        setDeveloper(null);
      }),
    []
  );

  const signIn = useCallback((token: string, dev: Developer) => {
    setToken(token);
    setDeveloper(dev);
    // ログイン / 登録の応答の developer には closedAt が無い（サーバーの応答の形。/v1/developers/me にだけある）。
    // WHY 取り直すか: 閉鎖済みの人が出金のためにログインしたとき、帯と「閉鎖済み」の説明が出ず、閉鎖の画面がもう一度出ていた
    // （2026-09-27 account-close の目視で発見）。失敗しても応答の値のまま表示は続ける（ログインは成功している）
    api<{ developer: Developer }>('/v1/developers/me')
      .then((r) => setDeveloper(r.developer))
      .catch(() => undefined);
  }, []);

  const signInConsole = useCallback(async (s: ConsoleSession) => {
    setAccountToken(s.accountToken);
    if (s.developerToken) {
      setToken(s.developerToken);
      // 作り手の情報を読んでから入る（帯・閉鎖の表示が要る）。読めなければ作り手の鍵は持たない = Player だけで入る（入れないよりよい）
      const dev = await api<{ developer: Developer }>('/v1/developers/me').then((r) => r.developer).catch(() => null);
      if (dev) setDeveloper(dev);
      else setToken(null);
    } else {
      // 前に別の人の作り手の鍵が残っていれば捨てる（作り手でない人が前の人の作り手の画面を見ない）
      setToken(null);
      setDeveloper(null);
    }
    setAccount({ token: s.accountToken, email: s.account.email, displayName: s.account.displayName });
  }, []);

  const dropAccount = useCallback(() => {
    setAccountToken(null);
    setAccount(null);
  }, []);

  const signOut = useCallback(async () => {
    // サーバー側のセッションも失効させる（端末に残ったトークンを無効化）。失敗してもローカルは消す。
    // kas_ も同じく失効させる（POST /v1/account/logout・この鍵だけ）。両方を並べて待つ
    const kas = getAccountToken();
    await Promise.all([
      getToken() ? api('/v1/developers/logout', { method: 'POST', body: {} }).catch(() => undefined) : undefined,
      kas ? logoutAccount(kas).catch(() => undefined) : undefined
    ]);
    setToken(null);
    setDeveloper(null);
    setAccountToken(null);
    setAccount(null);
  }, []);

  const updateDeveloper = useCallback((patch: Partial<Developer>) => setDeveloper((d) => (d ? { ...d, ...patch } : d)), []);
  const updateAccount = useCallback((patch: Partial<Omit<AccountSession, 'token'>>) => setAccount((a) => (a ? { ...a, ...patch } : a)), []);

  return (
    <Ctx.Provider value={{ developer, account, oneAccount, ready, signIn, signInConsole, dropAccount, signOut, updateDeveloper, updateAccount }}>{children}</Ctx.Provider>
  );
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
